/**
 * @fileoverview Environment configuration module for shared services
 * Handles environment-specific configuration without hardcoding environment access
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * 🚨 THERE IS NO DEFAULT BACKEND URL, AND ITS ABSENCE IS THE POINT.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * This module used to default `backendUrl` to a loopback address. That default
 * is only ever correct on a developer's own machine, and it is silent
 * everywhere else: `getBackendUrl()` is read by 10+ `fetch` call sites in
 * `chatApi.ts` and `embedApi.ts`, so an uninitialised read did not fail — it
 * sent every request to the VISITOR's own machine, which answers nothing. The
 * console showed connection errors with no hint that a configuration step had
 * been skipped.
 *
 * It also survived into the shipped bundle. `apps/frontend` resolves this
 * package to `src/` (see the `exports` map), so the literal was inlined into
 * `dist/assets/index-*.js` and reached every visitor of the public
 * `/embed/:embed-id` widget.
 *
 * A module-scoped default that is right on one machine and wrong everywhere
 * else is not a safety net; it is the shape that turns a missing configuration
 * step into a silent wrong destination. So there is no default now:
 * `getBackendUrl()` throws until `initializeEnvironment()` has supplied a real
 * value.
 *
 * ── Why a throw is safe here ─────────────────────────────────────────────────
 *
 * This module is part of `apps/frontend` and nothing outside the app imports it
 * (`NexusGPT/nexus-chat-embed` is a separate SPA that depends on
 * `@agent-nexus/sdk` and never on this code). The app calls
 * `initializeSharedConfig()` at the MODULE SCOPE of `src/store/index.ts`, a
 * store that is eager on every route including the public embed widget — so
 * initialisation already happens before any component can reach a `fetch`.
 *
 * That ordering was holding the old default harmless by coincidence. The throw
 * turns the coincidence into an invariant: if anyone ever code-splits the store
 * or reads a URL at module scope, this fails loudly at the first call instead of
 * quietly pointing production at a loopback address.
 *
 * The dev-time value is `apps/frontend`'s to supply, and it does: its own
 * `ENV.BACKEND_URL` carries the dev-server default through a `define` gated on
 * Vite's `command`, and a production build with no `VITE_BACKEND_URL` is
 * refused outright. One place decides that value; this module is not a second.
 */

/**
 * The uninitialised state. Not a usable URL, deliberately — see the note above.
 */
const defaultConfig = {
  backendUrl: "",
  isDevelopment: false,
  isEmbedApp: false
}

// Module-scoped configuration
let config = { ...defaultConfig }

/** A value that could actually be used as an origin. Blank and non-strings are not. */
function isUsableBackendUrl(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== ""
}

/**
 * Environment configuration interface for shared services
 */
export interface EnvironmentConfig {
  readonly backendUrl: string
  readonly isDevelopment?: boolean
  /**
   * Widens the embed-identity read in `resolveEmbedData` (`chatApi.ts`) from
   * "only when framed" to "framed OR this bundle is the standalone embed SPA",
   * so a direct top-level load of `/embed/<id>?sdata=…` picks identity up from
   * `window.location.search` rather than dropping it.
   *
   * 🚨 NOTHING IN THIS REPOSITORY SETS IT, AND THE SPA IT IS NAMED FOR CANNOT.
   * `initializeEnvironment()` is the only writer; nothing outside `apps/frontend`
   * imports this module (see the note at the top of this file), and the app's
   * `initializeSharedConfig()` — `apps/frontend/src/common/boot/index.ts` —
   * passes `backendUrl` and `isDevelopment` only, which is what keeps its
   * playground/admin pages ignoring URL identity params. The standalone SPA
   * lives in `NexusGPT/nexus-chat-embed`, which depends on `@agent-nexus/sdk`
   * and not on this app, so it has no `initializeEnvironment` to call.
   *
   * So `isEmbedApp()` is false in every bundle built from this repository, the
   * reader collapses to its framed branch, and the specs beside this file are
   * the only callers that pass `true`. That is why it is stated here rather
   * than left to be inferred: the field reads as a knob the embed SPA turns on,
   * and turning it on from that repository means that repository taking a
   * dependency on this code first.
   */
  readonly isEmbedApp?: boolean
}

/**
 * Initialize the shared services with environment-specific values
 * This should be called by consuming apps during initialization
 *
 * Omitting `backendUrl` preserves whatever is already configured, which is what
 * makes a second partial call a merge. Passing one that is blank or not a string
 * is refused HERE, at the cause, rather than at the first `fetch` several frames
 * later — the two are the same defect and only one of them is debuggable.
 */
export function initializeEnvironment(newConfig: EnvironmentConfig): void {
  if (newConfig.backendUrl !== undefined && !isUsableBackendUrl(newConfig.backendUrl)) {
    throw new Error(
      `initializeEnvironment() was given a backendUrl of ${JSON.stringify(
        newConfig.backendUrl
      )}, which is not a usable origin. Omit the field to keep the current value; ` +
        "pass a real origin to set one. It is never correct to configure a blank host — " +
        "every shared chat request would resolve against the page's own origin."
    )
  }

  config = {
    ...defaultConfig, // Apply defaults first
    ...config, // Preserve existing config (overrides defaults)
    // Only override with defined values (allow empty strings or null for environment variables)
    ...(newConfig.backendUrl !== undefined && { backendUrl: newConfig.backendUrl }),
    ...(newConfig.isDevelopment !== undefined && { isDevelopment: newConfig.isDevelopment }),
    ...(newConfig.isEmbedApp !== undefined && { isEmbedApp: newConfig.isEmbedApp })
  }
}

/**
 * Get the current environment configuration
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  return { ...config }
}

/**
 * Get the backend URL for API calls.
 *
 * @throws if `initializeEnvironment()` has not supplied one. There is no
 * fallback on purpose — see the note at the top of this file.
 */
export function getBackendUrl(): string {
  if (!isUsableBackendUrl(config.backendUrl)) {
    throw new Error(
      "The shared chat services have no backend URL, so no request can be addressed. " +
        "initializeEnvironment({ backendUrl }) has not run yet. In apps/frontend that call is " +
        "initializeSharedConfig(), at the module scope of src/store/index.ts — reaching a chat " +
        "request before that module is evaluated is the bug this reports. There is deliberately " +
        "no default: the previous one was a loopback address that shipped to every visitor."
    )
  }
  return config.backendUrl
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
  return config.isDevelopment ?? false
}

/**
 * Whether embed identity may be read from the URL on a top-level load.
 *
 * False in every bundle built from this repository — `EnvironmentConfig.isEmbedApp`
 * carries the reason, and it is the reason a caller needs before wiring anything to
 * this.
 */
export function isEmbedApp(): boolean {
  return config.isEmbedApp ?? false
}

/**
 * Reset configuration to defaults (useful for testing).
 *
 * This restores the UNINITIALISED state, so `getBackendUrl()` throws again until
 * a test supplies one. That is the same contract a freshly loaded module has.
 */
export function resetEnvironment(): void {
  config = { ...defaultConfig }
}
